import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldAlert,
  Search,
  Truck,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  Phone,
  MapPin,
  Calendar,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import {
  fetchCancellations,
  fetchShippingCarriers,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Stat } from '../ui/data.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd } from '../lib/format.js';


function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const clean = phone.trim();
  const digits = clean.replace(/\D/g, '');
  let d8 = digits;
  if (digits.startsWith('216') && digits.length === 11) {
    d8 = digits.slice(3);
  } else if (digits.length === 8) {
    d8 = digits;
  } else if (digits.length > 8) {
    d8 = digits.slice(-8);
  }
  if (d8.length === 8) {
    return `+216 ${d8.slice(0, 2)} ${d8.slice(2, 5)} ${d8.slice(5, 8)}`;
  }
  return clean;
}

export function CancellationsInvestigationPage(): React.ReactElement {
  const [carrier, setCarrier] = useState<string>('all');
  const [onlySuspiciousPaid, setOnlySuspiciousPaid] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const limit = 25;

  const carriersQuery = useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: fetchShippingCarriers,
  });

  const cancellationsQuery = useQuery({
    queryKey: ['shipping', 'cancellations', carrier, onlySuspiciousPaid, search, page],
    queryFn: () =>
      fetchCancellations({
        carrier: carrier === 'all' ? '' : carrier,
        onlySuspiciousPaid,
        search: search.trim(),
        page,
        limit,
      }),
  });

  const kpis = cancellationsQuery.data?.kpis;
  const parcels = cancellationsQuery.data?.parcels ?? [];
  const topMotifs = cancellationsQuery.data?.topMotifs ?? [];
  const totalCount = cancellationsQuery.data?.totalCount ?? 0;
  const totalPages = cancellationsQuery.data?.totalPages ?? 1;

  const suspiciousCount = kpis?.suspiciousPaidCount ?? 0;

  return (
    <div className="space-y-6 animate-fade">
      <PageHeader
        index="S39"
        eyebrow="Gestion Logistique · Enquête Annulations"
        title="Enquête Colis Annulés / Supprimés (État 6)"
        purpose="Investigation approfondie sur les motifs de suppression et détection prioritaire du schéma de vol (colis déclarés annulés mais encaissés)."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (parcels.length === 0) return;
                const headers = ['Code-barre', 'Client', 'Telephone', 'Ville', 'Montant', 'Presta', 'Motif', 'SuspectPaye'];
                const rows = parcels.map((p) => [
                  p.codeBarre,
                  `"${p.clientName ?? (p as unknown as Record<string, unknown>).nom ?? ''}"`,
                  `"${p.clientPhone ?? (p as unknown as Record<string, unknown>).tel ?? ''}"`,
                  `"${p.location ?? (p as unknown as Record<string, unknown>).ville ?? ''}"`,
                  p.shiparonPrice ?? (p as unknown as Record<string, unknown>).prix ?? '',
                  p.carrier ?? (p as unknown as Record<string, unknown>).presta ?? '',
                  `"${p.motif ?? ''}"`,
                  p.isSuspiciousPaid ? 'OUI_SUSPECT' : 'NON',
                ]);
                const csvContent = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `enquete_annulations_${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
              }}
              className="text-ink-soft hover:text-ink"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />
              Exporter CSV
            </Button>
          </div>
        }
      />
      
      {/* Critical Fraud Alert Banner if any Annulé is Paid */}
      {suspiciousCount > 0 ? (
        <div className="p-4 rounded-xl bg-rust-100 border-2 border-rust-600 text-rust-950 flex items-start justify-between gap-4 animate-fade shadow-sm">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg bg-rust-200 text-rust-900 shrink-0">
              <ShieldAlert className="h-6 w-6 text-rust-800" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base text-rust-900">
                  ALERTE DÉTOURNEMENT : {suspiciousCount} Colis Annulés mais ENCAISSÉS
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-extrabold bg-rust-700 text-white">
                  Fraude Potentielle
                </span>
              </div>
              <p className="text-xs text-rust-900 mt-1 leading-relaxed">
                Ces colis ont été classés en <strong>État 6 (Annulé / Supprimé)</strong> mais le transporteur a collecté et reversé les fonds (montant cumulé :{' '}
                <strong>{formatTnd(kpis?.suspiciousPaidAmount)}</strong>). C'est le schéma caractéristique où un colis livré est faussement déclaré supprimé pour dissimuler l'encaissement.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setOnlySuspiciousPaid(true);
              setPage(1);
            }}
            className="px-4 py-2 rounded-lg bg-rust-800 text-white font-bold text-xs hover:bg-rust-900 transition-colors shrink-0 shadow-xs"
          >
            Examiner ces {suspiciousCount} cas
          </button>
        </div>
      ) : (
        <div className="p-3.5 rounded-xl bg-herb-50 border border-herb-600/30 text-herb-900 flex items-center gap-3 text-xs">
          <ShieldAlert className="h-4 w-4 text-herb-700 shrink-0" />
          <span>
            <strong>Contrôle anti-détournement au vert :</strong> Aucun colis marqué "Annulé" n'a fait l'objet d'un paiement transporteur non justifié.
          </span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          label="Total Annulés / Supprimés"
          value={(kpis?.totalCancelled ?? 0).toLocaleString('fr-FR')}
          hint="Colis écartés du flux"
        />
        <Stat
          label="Annulés mais ENCAISSÉS"
          value={suspiciousCount.toLocaleString('fr-FR')}
          hint={suspiciousCount > 0 ? '⚠️ Schéma de vol' : 'Aucun litige'}
        />
        <Stat
          label="Montant Suspect Encaissé"
          value={formatTnd(kpis?.suspiciousPaidAmount ?? 0)}
          hint="Fonds à auditer"
        />
        <Stat
          label="Valeur Totale Annulée"
          value={formatTnd(kpis?.totalAmount ?? 0)}
          hint="Commandes abandonnées"
        />
      </div>

      {/* Motifs d'annulation fréquents */}
      {topMotifs.length > 0 && (
        <Panel>
          <div className="p-3.5 border-b border-border text-xs flex items-center justify-between">
            <span className="font-bold text-ink flex items-center gap-1.5">
              <HelpCircle className="h-4 w-4 text-emerald-700" />
              Répartition des Motifs d'Annulation Renseignés
            </span>
          </div>
          <div className="p-3 flex flex-wrap gap-2">
            {topMotifs.map((m) => (
              <span
                key={m.motif}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-cream text-ink text-xs border border-border"
              >
                <span className="font-medium">{m.motif}</span>
                <span className="font-bold text-ink-soft bg-white/80 px-1.5 py-0.2 rounded text-[11px]">
                  {m.count}
                </span>
              </span>
            ))}
          </div>
        </Panel>
      )}

      {/* Filters Toolbar */}
      <Panel>
        <div className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Quick Filter: All vs Suspicious Only */}
            <div className="flex items-center bg-cream/70 p-1 rounded-lg border border-border/80">
              <button
                type="button"
                onClick={() => {
                  setOnlySuspiciousPaid(false);
                  setPage(1);
                }}
                className={`px-3 py-1 text-xs rounded-md font-semibold transition-all ${
                  !onlySuspiciousPaid
                    ? 'bg-card text-ink shadow-2xs font-bold border border-border'
                    : 'text-ink-soft hover:text-ink'
                }`}
              >
                Tous les Annulés ({kpis?.totalCancelled ?? 0})
              </button>
              <button
                type="button"
                onClick={() => {
                  setOnlySuspiciousPaid(true);
                  setPage(1);
                }}
                className={`px-3 py-1 text-xs rounded-md font-semibold transition-all ${
                  onlySuspiciousPaid
                    ? 'bg-rust-700 text-white shadow-2xs font-bold'
                    : 'text-rust-800 hover:text-rust-950'
                }`}
              >
                🚨 Annulés mais PAYÉS ({suspiciousCount})
              </button>
            </div>

            {/* Search */}
            <div className="relative min-w-[220px]">
              <Search className="h-4 w-4 text-ink-ghost absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Rechercher code-barres, client, tel…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full text-xs pl-9 pr-3 py-1.5 rounded-lg border border-border bg-card text-ink focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none"
              />
            </div>

            {/* Carrier select */}
            <select
              value={carrier}
              onChange={(e) => {
                setCarrier(e.target.value);
                setPage(1);
              }}
              className="text-xs rounded-lg px-2.5 py-1.5 border border-border bg-card text-ink font-medium cursor-pointer"
            >
              <option value="all">Tous transporteurs</option>
              {carriersQuery.data?.carriers.map((c) => (
                <option key={c.carrier} value={c.carrier}>
                  {c.carrier} ({c.count})
                </option>
              ))}
            </select>
          </div>

          <div className="text-xs text-ink-soft">
            <strong>{totalCount.toLocaleString('fr-FR')}</strong> colis affiché(s)
          </div>
        </div>

        {/* Table of cancelled parcels */}
        <TableShell>
          <Table>
            <THead>
              <Tr>
                <Th>Code-Barres & Facture</Th>
                <Th>Client & Contact</Th>
                <Th>Destination</Th>
                <Th>Montant</Th>
                <Th>Transporteur</Th>
                <Th>Motif Enregistré</Th>
                <Th>Contrôle Financier</Th>
                <Th>Date</Th>
              </Tr>
            </THead>
            <TBody>
              {cancellationsQuery.isLoading ? (
                <Tr>
                  <Td colSpan={8} className="text-center py-8">
                    <Loading label="Chargement des annulations…" />
                  </Td>
                </Tr>
              ) : parcels.length === 0 ? (
                <Tr>
                  <Td colSpan={8} className="text-center py-8 text-ink-soft text-xs">
                    Aucun colis annulé trouvé avec ces filtres.
                  </Td>
                </Tr>
              ) : (
                parcels.map((parcel) => {
                  const clientName = parcel.clientName || (parcel as unknown as Record<string, unknown>).nom as string || 'Client';
                  const clientPhone = parcel.clientPhone || (parcel as unknown as Record<string, unknown>).tel as string || null;
                  const location = parcel.location || (parcel as unknown as Record<string, unknown>).ville as string || '—';
                  const price = parcel.shiparonPrice ?? (parcel as unknown as Record<string, unknown>).prix as number ?? null;
                  const presta = parcel.carrier || (parcel as unknown as Record<string, unknown>).presta as string || 'Non assigné';

                  return (
                    <Tr
                      key={parcel.codeBarre}
                      className={`transition-colors ${
                        parcel.isSuspiciousPaid
                          ? 'bg-rust-100/60 hover:bg-rust-100 font-medium'
                          : 'hover:bg-cream/40'
                      }`}
                    >
                      <Td className="font-mono font-bold text-xs text-ink">
                        <div className="flex items-center gap-1.5">
                          <XCircle className={`h-3.5 w-3.5 shrink-0 ${parcel.isSuspiciousPaid ? 'text-rust-700' : 'text-ink-soft'}`} />
                          <span>{parcel.codeBarre}</span>
                        </div>
                        {parcel.facture && (
                          <span className="text-[11px] text-ink-soft font-normal block">
                            Facture: {parcel.facture}
                          </span>
                        )}
                      </Td>
                      <Td className="text-xs">
                        <p className="font-semibold text-ink">{clientName}</p>
                        {clientPhone && (
                          <p className="text-[11px] text-ink-soft flex items-center gap-1 mt-0.5">
                            <Phone className="h-3 w-3 text-ink-ghost" />
                            {formatPhone(clientPhone)}
                          </p>
                        )}
                      </Td>
                      <Td className="text-xs text-ink-soft">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-ink-ghost shrink-0" />
                          {location}
                        </span>
                      </Td>
                      <Td className="text-xs font-bold text-ink whitespace-nowrap">
                        {formatTnd(price)}
                      </Td>
                      <Td className="text-xs font-medium text-ink">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-cream text-ink border border-border/80">
                          <Truck className="h-3 w-3 text-ink-soft" />
                          {presta}
                        </span>
                      </Td>
                    <Td className="text-xs text-ink-soft">
                      <span className="italic">{parcel.motif || 'Aucun motif renseigné'}</span>
                    </Td>
                    <Td className="text-xs whitespace-nowrap">
                      {parcel.isSuspiciousPaid ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-rust-700 text-white animate-pulse">
                          <ShieldAlert className="h-3 w-3" />
                          ANNULÉ MAIS PAYÉ !
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-cream text-ink-soft border border-border">
                          Non encaissé (Normal)
                        </span>
                      )}
                    </Td>
                    <Td className="text-xs text-ink-soft whitespace-nowrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-ink-ghost" />
                        {formatDate(parcel.date)}
                      </span>
                    </Td>
                  </Tr>
                );
              })
            )}
          </TBody>
          </Table>
        </TableShell>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between text-xs">
            <span className="text-ink-soft">
              Page {page} sur {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Suivant <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
